import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../lib/utils';
import { SectionHeader, EmptyState, StatBadge } from '../components/ui';
import {
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  FileText,
  ArrowRightLeft,
  Sparkles,
  Loader2,
} from 'lucide-react';

// ---- Types ----

type ItemType = 'action_item' | 'profile_field_suggestion' | 'summary';
type ItemStatus = 'pending' | 'approved' | 'rejected';

interface ReviewQueueItem {
  itemId: string;
  interactionId?: string;
  memberId: string;
  tenantId: string;
  itemType: ItemType;
  text?: string;
  dueDate?: string | null;
  field?: string;
  suggestedValue?: string;
  rationale?: string;
  status: ItemStatus;
  createdAt: string;
  decidedAt?: string;
  decidedBy?: string;
}

// ---- Mock data ----

const MOCK_ITEMS: ReviewQueueItem[] = [
  {
    itemId: 'rq-001',
    interactionId: 'int-abc',
    memberId: 'mem-001',
    tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    itemType: 'action_item',
    text: 'Follow up with Sarah Mitchell on HELOC application — she mentioned interest in refinancing.',
    dueDate: '2026-03-20',
    status: 'pending',
    createdAt: new Date(Date.now() - 25 * 60000).toISOString(),
  },
  {
    itemId: 'rq-002',
    interactionId: 'int-def',
    memberId: 'mem-002',
    tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    itemType: 'profile_field_suggestion',
    field: 'lifecycleStage',
    suggestedValue: 'at_risk',
    rationale: 'ops-scan:passed — Member missed 2 consecutive loan payments; AI recommends updating lifecycle stage.',
    status: 'pending',
    createdAt: new Date(Date.now() - 45 * 60000).toISOString(),
  },
  {
    itemId: 'rq-003',
    memberId: 'mem-003',
    tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    itemType: 'summary',
    text: 'Call summary for Priya Nair: Member inquired about CD maturity options and expressed interest in rolling over a 12-month CD at current rates. Recommend proactive rate comparison outreach.',
    status: 'pending',
    createdAt: new Date(Date.now() - 90 * 60000).toISOString(),
  },
  {
    itemId: 'rq-004',
    interactionId: 'int-ghi',
    memberId: 'mem-004',
    tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    itemType: 'action_item',
    text: 'Schedule a financial wellness review with Derek Fontaine — flagged as having high debt-to-income ratio.',
    dueDate: '2026-03-18',
    status: 'approved',
    createdAt: new Date(Date.now() - 3 * 3600000).toISOString(),
    decidedAt: new Date(Date.now() - 2 * 3600000).toISOString(),
    decidedBy: 'staff-k-chen',
  },
  {
    itemId: 'rq-005',
    interactionId: 'int-jkl',
    memberId: 'mem-006',
    tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    itemType: 'profile_field_suggestion',
    field: 'productInterestFlags',
    suggestedValue: 'mortgage_refinance',
    rationale: 'ops-scan:passed — James Tran referenced mortgage refinance twice during call; high confidence signal.',
    status: 'approved',
    createdAt: new Date(Date.now() - 5 * 3600000).toISOString(),
    decidedAt: new Date(Date.now() - 4 * 3600000).toISOString(),
    decidedBy: 'staff-l-park',
  },
  {
    itemId: 'rq-006',
    memberId: 'mem-005',
    tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    itemType: 'summary',
    text: 'Session summary for Linda Okafor: AI-generated content flagged due to low confidence scores. Recommend manual review of raw transcript.',
    status: 'rejected',
    createdAt: new Date(Date.now() - 8 * 3600000).toISOString(),
    decidedAt: new Date(Date.now() - 7 * 3600000).toISOString(),
    decidedBy: 'staff-j-adams',
  },
];

// ---- Helpers ----

function getJwt(): string {
  return localStorage.getItem('crm_dev_token') ?? '';
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ---- Item type color map ----

interface TypeMeta {
  label: string;
  cls: string;
  Icon: typeof Clock;
}

const TYPE_MAP: Record<ItemType, TypeMeta> = {
  action_item: {
    label: 'Action Item',
    cls: 'bg-warn-soft text-warn border border-warn-soft',
    Icon: Clock,
  },
  profile_field_suggestion: {
    label: 'Field Suggestion',
    cls: 'bg-running-soft text-running-ink border border-running-soft',
    Icon: ArrowRightLeft,
  },
  summary: {
    label: 'Summary',
    cls: 'bg-brand-tint text-brand-dark border border-info-soft',
    Icon: FileText,
  },
};

// ---- ReviewQueueCard ----

interface ReviewQueueCardProps {
  item: ReviewQueueItem;
  actionInProgress: string | null;
  onApprove: (itemId: string) => void;
  onReject: (itemId: string) => void;
}

function ReviewQueueCard({
  item,
  actionInProgress,
  onApprove,
  onReject,
}: ReviewQueueCardProps): React.ReactElement {
  const typeMeta = TYPE_MAP[item.itemType];
  const { Icon: TypeIcon } = typeMeta;
  const isProcessing = actionInProgress === item.itemId;

  return (
    <div className="bg-white rounded-xl border border-line shadow-sm p-4 flex flex-col gap-3">
      {/* Top row: type badge + created date + member pill */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
            typeMeta.cls,
          )}
        >
          <TypeIcon className="h-3 w-3" aria-hidden />
          {typeMeta.label}
        </span>
        <span className="text-xs text-ink-muted">{fmtRelative(item.createdAt)}</span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sunken text-ink-soft ml-auto">
          <User className="h-3 w-3" aria-hidden />
          {item.memberId}
        </span>
      </div>

      {/* Content area */}
      <div className="text-sm text-ink-soft">
        {item.itemType === 'profile_field_suggestion' ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-ink-muted uppercase tracking-wide">Field</span>
              <span className="font-medium text-ink">{item.field ?? '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-ink-muted uppercase tracking-wide">Suggested</span>
              <span
                className="inline-flex items-center px-2 py-0.5 rounded bg-running-soft text-running-ink text-xs font-medium border border-running-soft"
              >
                {item.suggestedValue ?? '—'}
              </span>
            </div>
            {item.rationale !== undefined && (
              <p className="text-xs text-ink-muted mt-1 flex items-start gap-1.5">
                <Sparkles className="h-3 w-3 mt-0.5 flex-shrink-0 text-running" aria-hidden />
                {item.rationale}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <p className="leading-relaxed">{item.text}</p>
            {item.itemType === 'action_item' && item.dueDate != null && (
              <p className="flex items-center gap-1.5 text-xs text-warn">
                <Clock className="h-3 w-3" aria-hidden />
                Due {fmtDate(item.dueDate)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Action / status area */}
      {item.status === 'pending' ? (
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            disabled={isProcessing}
            onClick={() => onApprove(item.itemId)}
            className="inline-flex items-center gap-1.5 bg-pass text-white hover:opacity-90 disabled:opacity-60 rounded-lg px-4 py-2 text-sm font-medium transition-opacity"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <CheckCircle2 className="h-4 w-4" aria-hidden />
            )}
            Approve
          </button>
          <button
            type="button"
            disabled={isProcessing}
            onClick={() => onReject(item.itemId)}
            className="inline-flex items-center gap-1.5 bg-white text-fail border border-fail-soft hover:bg-fail-soft disabled:opacity-60 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <XCircle className="h-4 w-4" aria-hidden />
            )}
            Reject
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 pt-1">
          {item.status === 'approved' ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-pass-soft text-pass border border-pass-soft">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              Approved
              {item.decidedAt !== undefined && (
                <span className="text-ink-muted font-normal ml-0.5">· {fmtDate(item.decidedAt)}</span>
              )}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-fail-soft text-fail border border-fail-soft">
              <XCircle className="h-3.5 w-3.5" aria-hidden />
              Rejected
              {item.decidedAt !== undefined && (
                <span className="text-ink-muted font-normal ml-0.5">· {fmtDate(item.decidedAt)}</span>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ---- StaffReviewQueuePage ----

type FilterValue = 'pending' | 'decided' | 'all';

const FILTER_OPTIONS: { value: FilterValue; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'decided', label: 'Decided' },
  { value: 'all', label: 'All' },
];

export function StaffReviewQueuePage(): React.ReactElement {
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterValue>('pending');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);

  // ---- Fetch ----

  const fetchQueue = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await fetch('/api/review-queue', {
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as ReviewQueueItem[];
      setItems(data);
    } catch {
      // API unavailable in dev — use mock data
      setItems(MOCK_ITEMS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchQueue();
  }, [fetchQueue]);

  // ---- Approve / Reject ----

  const handleApprove = useCallback(async (itemId: string): Promise<void> => {
    setActionInProgress(itemId);
    setItemError(null);
    try {
      const res = await fetch(`/api/review-queue/${itemId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) =>
        prev.map((i) =>
          i.itemId === itemId
            ? { ...i, status: 'approved' as ItemStatus, decidedAt: new Date().toISOString() }
            : i,
        ),
      );
    } catch {
      setItemError(`Failed to approve item ${itemId}. Please try again.`);
    } finally {
      setActionInProgress(null);
    }
  }, []);

  const handleReject = useCallback(async (itemId: string): Promise<void> => {
    setActionInProgress(itemId);
    setItemError(null);
    try {
      const res = await fetch(`/api/review-queue/${itemId}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) =>
        prev.map((i) =>
          i.itemId === itemId
            ? { ...i, status: 'rejected' as ItemStatus, decidedAt: new Date().toISOString() }
            : i,
        ),
      );
    } catch {
      setItemError(`Failed to reject item ${itemId}. Please try again.`);
    } finally {
      setActionInProgress(null);
    }
  }, []);

  // ---- Derived state ----

  const filteredItems = items.filter((i) => {
    if (activeFilter === 'pending') return i.status === 'pending';
    if (activeFilter === 'decided') return i.status !== 'pending';
    return true;
  });

  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const approvedCount = items.filter((i) => i.status === 'approved').length;
  const rejectedCount = items.filter((i) => i.status === 'rejected').length;

  // ---- Render ----

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div>
        <SectionHeader title="Staff Review Queue" />
        <p className="text-sm text-ink-muted mt-0.5">AI-generated content awaiting staff review</p>
      </div>

      {/* Error banner */}
      {itemError !== null && (
        <div className="px-4 py-3 bg-fail-soft border border-fail-soft rounded-lg text-sm text-fail">
          {itemError}
        </div>
      )}

      {/* Stats row */}
      <div className="flex flex-wrap gap-2">
        <StatBadge count={pendingCount} label="Pending" color="amber" />
        <StatBadge count={approvedCount} label="Approved" color="teal" />
        <StatBadge count={rejectedCount} label="Rejected" color="red" />
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setActiveFilter(opt.value)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeFilter === opt.value
                ? 'bg-running text-white'
                : 'bg-sunken text-ink-soft hover:bg-line',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Items list */}
      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-line shadow-sm p-4 animate-pulse"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="h-5 bg-line rounded-full w-24" />
                <div className="h-4 bg-sunken rounded w-16" />
              </div>
              <div className="h-4 bg-line rounded w-full mb-2" />
              <div className="h-4 bg-sunken rounded w-3/4 mb-4" />
              <div className="flex gap-2">
                <div className="h-8 bg-line rounded-lg w-24" />
                <div className="h-8 bg-sunken rounded-lg w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-xl border border-line shadow-sm p-8">
          <EmptyState
            icon={ClipboardCheck}
            headline="No items to review"
            body="AI-generated content will appear here as staff interact with the system."
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredItems.map((item) => (
            <ReviewQueueCard
              key={item.itemId}
              item={item}
              actionInProgress={actionInProgress}
              onApprove={(id) => { void handleApprove(id); }}
              onReject={(id) => { void handleReject(id); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
